// eventBus.js
import mitt from "mitt";
export const eventBus = mitt();

/** 存 token */
export function setToken(token: string) {
  localStorage.setItem("token", token);
}

/** 取 token */
export function getToken(): string | null {
  return localStorage.getItem("token");
}

/** 删 token */
export function removeToken() {
  localStorage.removeItem("token");
}

/** 存权限（示例：AntD Pro 的权限管理） */
export function setAuthority(authority: string | string[]) {
  const list = typeof authority === "string" ? [authority] : authority;
  localStorage.setItem("antd-pro-authority", JSON.stringify(list));
}

/** 取权限 */
export function getAuthority(): string[] {
  const str = localStorage.getItem("antd-pro-authority") || "[]";
  try {
    return JSON.parse(str);
  } catch {
    return [];
  }
}

/** 获取 API URL */
export function getUrl(path: string): string {
  const isDev = process.env.NODE_ENV === "development";
  const baseUrl = isDev ? "/proxyApi/admin/v1" : "/api/admin/v1";
  return baseUrl + path;
}

/**
 * 字段扩展
 * @param dataArr 源对象数组
 * @param fieldObject 需要扩展的字段{key:转为？}
 *
 */
export const fieldExtend = (
  dataArr: any,
  fieldObject: any,
  merge: any = true
) => {
  let newDataArr = [];
  for (let i = 0, len = dataArr.length; i < len; i++) {
    let objItem: any = {};
    for (let key in fieldObject) {
      if (dataArr[i][key] !== undefined) {
        dataArr[i][fieldObject[key]] = dataArr[i][key];
        if (!merge) {
          objItem[fieldObject[key]] = dataArr[i][key];
        }
      }
    }
    if (!merge) {
      newDataArr.push(objItem);
    }
  }
  return merge ? dataArr : newDataArr;
};

/**
 * copy text
 */
export const copyText = async (str: any) => {
  if (navigator?.clipboard) {
    try {
      await navigator.clipboard.writeText(str);
      console.log("Text copied to clipboard");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  } else {
    console.error("Clipboard API not supported");
    if (!window || (window && !window.document))
      return console.error("window not init");
    const input: any = document.createElement("input");
    const body: any = document.querySelector("body");
    body.append(input);
    input.value = str;
    input.select();
    document.execCommand("copy");
    input.remove();
  }
};
/**
 * 单个查找对象数组中的某个对象，返回第一个找到的对象及index
 * param obj  //要查找的对象{key>value},暂时只支持输入一个key+value进行搜索
 * param array //对象数组源数据
 * return {obj,index}找到返回对应对象及index，未找到返回-1
 */
export const objArrayIndexOf = (obj: any, array: any) => {
  let res: any = -1;
  if (!array.length || !Object.keys(obj).length) {
    console.log("公共方法Array.indexOf(obj,array)参数不能为空，请检查！");
    console.log("obj:" + obj);
    console.log("array:" + array);
    return;
  }
  let key = Object.keys(obj)[0];
  for (let i = 0, len = array.length; i < len; i++) {
    if (array[i][key] === obj[key]) {
      res = { resObj: array[i], index: i };
      break;
    }
  }
  return res;
};

/**
 * 创建简化列配置的工具函数
 * @param columns 列配置数组
 */
export const toColums = (columns: any[]) => {
  return columns.map((column) => {
    const { title, ...otherProps } = column;
    return {
      title,
      fieldProps: {
        placeholder: title,
      },
      ...otherProps,
    };
  });
};

/**
 * rename tree Data
 */
export const reTreeFields = (fieldMap: any, data: any) => {
  // Helper function to recursively rename fields in an object
  function renameObjectFields(fieldMap: any, obj: any) {
    let newObj: any = {};
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        let newKey = fieldMap[key] || key;
        if (typeof newKey === "function") {
          newObj[newKey.name] = newKey(obj);
        } else if (Array.isArray(obj[key])) {
          newObj[newKey] = obj[key].map((item: any) =>
            typeof item === "object" ? renameObjectFields(fieldMap, item) : item
          );
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          newObj[newKey] = renameObjectFields(fieldMap, obj[key]);
        } else {
          newObj[newKey] = obj[key];
        }
      }
    }

    // Add new composite fields
    for (let compositeKey in fieldMap) {
      if (typeof fieldMap[compositeKey] === "function") {
        newObj[compositeKey] = fieldMap[compositeKey](obj);
      }
    }

    return newObj;
  }

  // Process each item in the data array
  return data.map((item: any) => renameObjectFields(fieldMap, item));
};

export const isPlainObject = (x: any) =>
  Object.prototype.toString.call(x) === "[object Object]";

export const objectToList = (obj: Record<string, any> | undefined | null) => {
  if (!obj || Array.isArray(obj)) return (obj as any) || [];
  return Object.entries(obj).map(([key, value]) => ({ key, value }));
};
export const listToObject = (
  list: Array<{ key?: string; value?: any }> | undefined | null
) => {
  const result: Record<string, any> = {};
  (list || []).forEach((item) => {
    const k = (item?.key ?? "").toString().trim();
    if (!k) return;
    // 后写覆盖前写，避免重复 key 报错；如需严格校验可在这里抛错或过滤
    result[k] = item?.value;
  });
  return result;
};

/** 把 values 根据 formConfig 规范化：凡是 collapse+object 的字段，回填前转成数组 */
export const normalizeValuesForForm = (values: any, schema: any[]): any => {
  if (!values) return values;
  const out = { ...values };

  const walk = (items: any[], prefix?: (string | number)[]) => {
    items?.forEach((it: any) => {
      const key = it?.dataIndex;
      if (!key) return;

      // 当前层路径
      const curPath = prefix ? [...prefix, key] : [key];
      // 取值（顶层直接用 out）
      let curValParent = out as any;
      for (let i = 0; i < curPath.length - 1; i++) {
        const seg = curPath[i];
        if (curValParent?.[seg] == null) return; // 没值就不用处理
        curValParent = curValParent[seg];
      }
      const last = curPath[curPath.length - 1];

      // 1) collapse + object：对象→数组
      if (it?.type === "collapse" && it?.dataFormat === "object") {
        const raw = curValParent?.[last];
        if (isPlainObject(raw)) {
          curValParent[last] = objectToList(raw);
        }
      }

      // 2) uniqueView：里面实际还是对象/嵌套，递归处理 formChildren
      if (it?.formChildren) {
        // 确保对象存在再递归
        if (curValParent?.[last]) {
          walk(it.formChildren, curPath);
        }
      }

      // 3) 普通 children（collapse/compound）也递归
      if (it?.children) {
        if (curValParent?.[last]) {
          walk(it.children, curPath);
        }
      }
    });
  };

  walk(schema);
  return out;
};
